import { dealCtaLabel } from '@/lib/deal-cta'

/** Price display for the homepage: $1,997 · $2,379.98 — cents only when they are not .00. */
export function formatPrice(cents: number): string {
  if (!Number.isFinite(cents) || cents <= 0) return ''
  const dollars = Math.floor(cents / 100)
  const rem = cents % 100
  const base = dollars.toLocaleString('en-US')
  return rem ? `$${base}.${String(rem).padStart(2, '0')}` : `$${base}`
}

/** Split a price's WHOLE dollars into the digit columns the hero's digit-roll renders (thousands and
 *  hundreds groups). The cents are their own columns (`priceCentsDigits`), so a $1,799.99 offer rolls
 *  to "$1,799.99" — never rounded up to a "$1,800" the retailer is not charging. */
export function priceDigits(cents: number): string[] {
  const dollars = Math.floor(Math.max(0, Math.round(cents)) / 100)
  return dollars.toLocaleString('en-US').split(',')
}

/** The two cent digits the roll prints after the point, or null for a whole-dollar price — the same
 *  rule formatPrice applies ("$949", "$2,705.08"). */
export function priceCentsDigits(cents: number): string | null {
  const rem = Math.max(0, Math.round(cents)) % 100
  return rem ? String(rem).padStart(2, '0') : null
}

/**
 * FRIDGE-796 — the accessible name of every deal link: the visible
 * words first (WCAG 2.5.3 Label in Name), then the product, its model (the FIN
 * when there is no model) and its price, so no two deal links on a page share
 * a name unless they share a destination.
 *
 * The model qualifier is there for the same reason `productLinkName` carries
 * one: variants of one product ship with the SAME brand, display title and
 * price. On 09/07/2026 the French-door collection's "New Arrivals" rail held
 * two Kucht 72 in. built-ins (KR720FDSS / KR720FDGL) and two Inster 36 in.
 * units (YLXUEQI9001 / 9002) — two names, each resolving to two URLs (WCAG
 * 2.4.4) — and the axe link-purpose ratchet tripped on a pull request that
 * never touched the page. Which products sit in that rail changes by the hour,
 * so the name itself has to be unique, not the data. The model and the FIN are
 * both printed on the card, so nothing is announced that a sighted reader
 * cannot see. Callers without a model or FIN (the saved/compare panels before
 * they learn the model) get the old shape, unchanged.
 *
 * Lives here, not in product-card.tsx, because the saved/compare panels use it
 * too and product-card imports them (a helper in product-card would be a cycle).
 */
export function dealLinkName(p: {
  brand: string
  title: string
  priceCents: number | null
  model?: string | null
  fin?: string | null
}): string {
  const qualifier = p.model?.trim() ? `, model ${p.model.trim()}` : p.fin?.trim() ? `, ${p.fin.trim()}` : ''
  const renderedPrice = p.priceCents == null ? '' : formatPrice(p.priceCents)
  const price = renderedPrice ? `, ${renderedPrice}` : ''
  return `${dealCtaLabel(renderedPrice)}: ${p.brand} ${p.title}${qualifier}${price}`
}

/**
 * FRIDGE-796 — the accessible name of a card's product-title link. Two products
 * can carry the SAME display title once `displayTitle` strips the brand prefix
 * and the trailing model: on 09/05/2026 the home page shipped two GE top
 * freezers both reading "28 in. 17.5 cu. ft. Top Freezer Refrigerator", which
 * is one name resolving to two URLs (WCAG 2.4.4). The visible text stays first
 * so the name still contains the label (WCAG 2.5.3); the brand and the model —
 * the FIN when there is no model — make it unique, and both are already printed
 * on the card, so nothing is announced that a sighted reader cannot see.
 */
export function productLinkName(p: { brand: string; title: string; model: string | null; fin: string }): string {
  // FRIDGE-871: /deals renders rows whose canonical row can be missing a FIN, so
  // the qualifier can be empty. An empty one is DROPPED rather than joined —
  // "Title — Brand," with a dangling comma is a worse name than "Title — Brand".
  const qualifier = p.model ? `model ${p.model}` : p.fin.trim()
  const brand = p.brand.trim()
  const tail = [brand, qualifier].filter(Boolean).join(', ')
  return tail ? `${p.title} — ${tail}` : p.title
}

/** Match the CTA to the same valid cents value the homepage price display accepts. */
export function homeDealCtaLabel(priceCents: number | null): string {
  return dealCtaLabel(priceCents == null ? '' : formatPrice(priceCents))
}

/** Compact comparison copy only; product catalog titles remain unchanged. */
export function comparisonTitle(title: string): string {
  return title
    .replace(/(Smart Counter-Depth MAX French Door Refrigerator), 27 cu\. ft\.$/, '$1')
    .replace('30 CuFt French Door Refrigerator', '30 Cu Ft French Door Refrigerator')
}
